import type {
  AuthResponse,
  LoginInput,
  PublicUser,
  RegisterInput,
} from "@animeshadow/shared";
import { readReferral } from "@/lib/referral";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRequest, setAuthToken, ApiRequestError } from "@/lib/api";
import { queryClient } from "@/lib/query";
import type { TelegramAuthData } from "@/lib/telegram-auth";

const STORAGE_KEY = "animeshadow.auth.v1";

interface StoredSession {
  v: 1;
  token: string;
  user: PublicUser;
}

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: PublicUser | null;
  login: (input: LoginInput) => Promise<void>;
  /** Returns true when direct signup created a session; false when a code is required. */
  requestRegistration: (input: RegisterInput) => Promise<boolean>;
  /** Step 2 — the code was right, the account now actually exists, and this
   * applies the session exactly like `login` does. */
  /** `password`: only when the server asks for it (PASSWORD_REQUIRED) —
   *  the code is being confirmed in a browser without the signup ticket. */
  confirmRegistration: (email: string, code: string, password?: string) => Promise<void>;
  loginWithTelegram: (data: TelegramAuthData) => Promise<void>;
  loginWithGoogle: (credential: string, acceptTerms?: boolean) => Promise<void>;
  logout: () => void;
  /** Patch the cached user (e.g. after an avatar/name change) without a full re-login. */
  updateUser: (patch: Partial<PublicUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed?.v !== 1 || !parsed.token || !parsed.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session: StoredSession | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / storage disabled — session stays in memory only */
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const applySession = useCallback((session: StoredSession) => {
    setAuthToken(session.token);
    writeSession(session);
    setUser(session.user);
    setStatus("authenticated");
  }, []);

  const updateUser = useCallback((patch: Partial<PublicUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      const session = readSession();
      if (session) writeSession({ ...session, user: next });
      return next;
    });
  }, []);

  const clearSession = useCallback(() => {
    setAuthToken(null);
    writeSession(null);
    setUser(null);
    setStatus("anonymous");
    queryClient.removeQueries({ queryKey: ["library"] });
  }, []);

  // Restore + revalidate a stored session on load.
  useEffect(() => {
    const session = readSession();
    if (!session) {
      setStatus("anonymous");
      return;
    }
    setAuthToken(session.token);
    setUser(session.user);
    setStatus("authenticated");

    // Only the server saying "this token is no good" ends a session. A
    // network error or a 5xx — the API restarting, a proxy hiccup, a bad
    // connection — used to land in the same catch and sign people out,
    // which is how a two-second deploy logged everyone out of the site.
    // Those keep the stored session and simply try again.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = (attempt: number) => {
      apiRequest<{ user: PublicUser }>("/auth/me")
        .then(({ user: fresh }) => {
          if (!cancelled) applySession({ ...session, user: fresh });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          const status = error instanceof ApiRequestError ? error.status : 0;
          if (status === 401 || status === 403) {
            clearSession();
            return;
          }
          if (attempt < 5) timer = setTimeout(() => check(attempt + 1), 2_000 * (attempt + 1));
        });
    };
    check(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [applySession, clearSession]);

  const login = useCallback(
    async (input: LoginInput) => {
      const res = await apiRequest<AuthResponse>("/auth/login", {
        method: "POST",
        body: input,
      });
      applySession({ v: 1, token: res.token, user: res.user });
    },
    [applySession],
  );

  const requestRegistration = useCallback(async (input: RegisterInput) => {
    // Both temporary direct signup (201) and the optional code flow (202).
    const res = await apiRequest<AuthResponse | { ticket: string }>("/auth/register", { method: "POST", body: input });
    if ("token" in res) {
      writeSignupTicket(input.email, null);
      applySession({ v: 1, token: res.token, user: res.user });
      return true;
    }
    writeSignupTicket(input.email, res.ticket);
    return false;
  }, [applySession]);

  const confirmRegistration = useCallback(
    async (email: string, code: string, password?: string) => {
      const ticket = readSignupTicket(email);
      const res = await apiRequest<AuthResponse>("/auth/register/confirm", {
        method: "POST",
        body: { email, code, ...(ticket ? { ticket } : {}), ...(password ? { password } : {}) },
      });
      writeSignupTicket(email, null);
      applySession({ v: 1, token: res.token, user: res.user });
    },
    [applySession],
  );

  const loginWithTelegram = useCallback(
    async (data: TelegramAuthData) => {
      const referralCode = readReferral();
      const res = await apiRequest<AuthResponse>("/auth/telegram", {
        method: "POST",
        // The friend's code rides next to Telegram's signed fields, not in them.
        body: referralCode ? { ...data, referralCode } : data,
      });
      applySession({ v: 1, token: res.token, user: res.user });
    },
    [applySession],
  );

  const loginWithGoogle = useCallback(
    async (credential: string, acceptTerms = false) => {
      const referralCode = readReferral();
      const res = await apiRequest<AuthResponse>("/auth/google", {
        method: "POST",
        body: { credential, acceptTerms, ...(referralCode ? { referralCode } : {}) },
      });
      applySession({ v: 1, token: res.token, user: res.user });
    },
    [applySession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login,
      requestRegistration,
      confirmRegistration,
      loginWithTelegram,
      loginWithGoogle,
      logout: clearSession,
      updateUser,
    }),
    [
      status,
      user,
      login,
      requestRegistration,
      confirmRegistration,
      loginWithTelegram,
      loginWithGoogle,
      clearSession,
      updateUser,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

const FALLBACK_AUTH: AuthContextValue = {
  status: "anonymous",
  user: null,
  login: async () => {},
  requestRegistration: async () => false,
  confirmRegistration: async () => {},
  loginWithTelegram: async () => {},
  loginWithGoogle: async () => {},
  logout: () => {},
  updateUser: () => {},
};

export function useAuth(): AuthContextValue {
  return use(AuthContext) ?? FALLBACK_AUTH;
}

/**
 * The signup ticket, per email, in localStorage — not sessionStorage: the
 * one-click link in the mail opens a new tab, and it should still find it.
 */
const TICKET_KEY = "animeshadow.signupTicket";

function readSignupTicket(email: string): string | null {
  try {
    const stored = JSON.parse(localStorage.getItem(TICKET_KEY) ?? "null") as { email?: string; ticket?: string } | null;
    return stored?.email === email.trim().toLowerCase() ? (stored.ticket ?? null) : null;
  } catch {
    return null;
  }
}

function writeSignupTicket(email: string, ticket: string | null): void {
  try {
    if (ticket) localStorage.setItem(TICKET_KEY, JSON.stringify({ email: email.trim().toLowerCase(), ticket }));
    else localStorage.removeItem(TICKET_KEY);
  } catch {
    /* storage blocked — the password confirms it instead */
  }
}
